import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BaseHttpService } from '@shared/services/base-http/base-http.service';
import { Observable } from 'rxjs';
import {
  InvestmentDetailsResponse,
  InvestmentsListResponse,
  InvestmentsSummaryResponse,
} from './investments.interfaces';

/**
 * Investments service
 */
@Injectable({ providedIn: 'root' })
export class InvestmentsService {
  /**
   * Base HTTP service for API calls
   */
  private readonly baseHttpService = inject(BaseHttpService);

  /**
   * Get investments summary
   *
   * @param cnpj - Client CNPJ
   * @returns Observable with investments summary
   */
  public getSummary(cnpj: string): Observable<InvestmentsSummaryResponse> {
    const params = new HttpParams().set('client_cnpj', cnpj);
    return this.baseHttpService.get<InvestmentsSummaryResponse>(
      'api/v1/investments/summary',
      params,
    );
  }

  /**
   * Get fixed income investments
   *
   * @param cnpj - Client CNPJ
   * @param page - Page number (default: 1)
   * @param pageSize - Page size (default: 20)
   * @param sortBy - Optional sort field
   * @param sortOrder - Optional sort order
   * @returns Observable with fixed income investments list
   */
  public getFixedIncomeInvestments(
    cnpj: string,
    page = 1,
    pageSize = 20,
    sortBy?: string,
    sortOrder?: string,
  ): Observable<InvestmentsListResponse> {
    let params = new HttpParams()
      .set('client_cnpj', cnpj)
      .set('page', page.toString())
      .set('page_size', pageSize.toString());

    if (sortBy) {
      params = params.set('sort_by', sortBy);
    }

    if (sortOrder) {
      params = params.set('sort_order', sortOrder);
    }

    return this.baseHttpService.get<InvestmentsListResponse>(
      'api/v1/investments/fixed-income',
      params,
    );
  }

  /**
   * Get variable income investments
   *
   * @param cnpj - Client CNPJ
   * @param page - Page number (default: 1)
   * @param pageSize - Page size (default: 20)
   * @param sortBy - Optional sort field
   * @param sortOrder - Optional sort order
   * @returns Observable with variable income investments list
   */
  public getVariableIncomeInvestments(
    cnpj: string,
    page = 1,
    pageSize = 20,
    sortBy?: string,
    sortOrder?: string,
  ): Observable<InvestmentsListResponse> {
    let params = new HttpParams()
      .set('client_cnpj', cnpj)
      .set('page', page.toString())
      .set('page_size', pageSize.toString());

    if (sortBy) {
      params = params.set('sort_by', sortBy);
    }

    if (sortOrder) {
      params = params.set('sort_order', sortOrder);
    }

    return this.baseHttpService.get<InvestmentsListResponse>(
      'api/v1/investments/variable-income',
      params,
    );
  }

  /**
   * Get investment funds
   *
   * @param cnpj - Client CNPJ
   * @param page - Page number (default: 1)
   * @param pageSize - Page size (default: 20)
   * @param sortBy - Optional sort field
   * @param sortOrder - Optional sort order
   * @returns Observable with investment funds list
   */
  public getInvestmentFunds(
    cnpj: string,
    page = 1,
    pageSize = 20,
    sortBy?: string,
    sortOrder?: string,
  ): Observable<InvestmentsListResponse> {
    let params = new HttpParams()
      .set('client_cnpj', cnpj)
      .set('page', page.toString())
      .set('page_size', pageSize.toString());

    if (sortBy) {
      params = params.set('sort_by', sortBy);
    }

    if (sortOrder) {
      params = params.set('sort_order', sortOrder);
    }

    return this.baseHttpService.get<InvestmentsListResponse>(
      'api/v1/investments/investment-funds',
      params,
    );
  }

  /**
   * Get investment details by investment ID
   *
   * @param investmentId - Investment ID to get details for
   * @param clientCnpj - Client CNPJ for authentication
   *
   * @returns Observable with investment details
   */
  public getInvestmentDetails(
    investmentId: string,
    clientCnpj: string,
  ): Observable<InvestmentDetailsResponse> {
    const params = new HttpParams().set('client_cnpj', clientCnpj);
    return this.baseHttpService.get<InvestmentDetailsResponse>(
      `api/v1/investments/${investmentId}`,
      params,
    );
  }
}
